import { Module } from "vuex";
import gradesService from "@/services/gradesService";
import { Grade, GradeQueryParams } from "@/interfaces/Grade";
import { Platform } from "@/interfaces/Platform";

interface GradesState {
  grades: Grade[];
  filters: object;
  selectedPlatform: Platform | null;
  loading: boolean;
  loadingGradeId: number | null;
  params: GradeQueryParams; // Query parameters for API
  meta: PaginationMeta; // Pagination metadata
  error: string | null; // Error handling
}

const gradesModule: Module<GradesState, any> = {
  namespaced: true,

  state: (): GradesState => ({
    grades: [],
    filters: {},
    selectedPlatform: null,
    loading: false,
    loadingGradeId: null,
    params: {
      name: "",
      round: undefined,
      status: undefined,
      sort: "id",
      order: "asc",
      page: 1,
    },
    meta: {
      current_page: 0,
      last_page: 0,
      per_page: 0,
      total: 0,
      first_page_number: 1,
      last_page_number: 0,
      next_page_number: null,
      prev_page_number: null,
      pages: [],
    },
    error: null,
  }),

  mutations: {
    setGrades(state, grades: Grade[]) {
      state.grades = grades;
    },
    setFilters(state, filters: object) {
      state.filters = filters;
    },
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setLoadingGrade(state, gradeId: number | null) {
      state.loadingGradeId = gradeId;
    },
    setParams(state, params: Partial<GradesState["params"]>) {
      state.params = { ...state.params, ...params };
    },
    setPaginationMeta(state, meta: PaginationMeta) {
      state.meta = meta;
    },
    setError(state, error: string | null) {
      state.error = error;
    },
  },

  actions: {
    async fetchGrades({ commit, state }, { url }) {
      commit("setLoading", true);
      try {
        const response = await gradesService.getGrades(url, state.params);
        commit("setGrades", response.data.data);
        commit("setFilters", response.data.filters || {}); 
        commit("setPaginationMeta", response.data.meta);
      } catch (error) {
        commit("setError", error || "Failed to fetch grades");
      } finally {
        commit("setLoading", false);
      }
    },

    updateParams({ commit, dispatch }, { url, newParams }) {
      commit("setParams", newParams);
      dispatch("fetchGrades", { url });
    },
  },

  getters: {
    allGrades: (state) => state.grades,
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    errorMessage: (state) => state.error,
    loadingGradeId: (state) => state.loadingGradeId,
    params: (state) => state.params,
    filters: (state) => state.filters,
  },
};

export default gradesModule;
